import {
  Configuration,
  InteractionRequiredAuthError,
  PublicClientApplication,
} from "@azure/msal-browser";

const clientId: string = process.env.REACT_APP_CLIENT_ID ?? "";
const tenantId: string = process.env.REACT_APP_TENANT_ID ?? "";

// Auth is feature-flagged: when REACT_APP_CLIENT_ID is empty/undefined the app
// behaves exactly as before (no login, no Authorization headers).
export const authEnabled: boolean = Boolean(clientId);

export const apiScope: string = process.env.REACT_APP_API_SCOPE ?? "";
export const orchestratorApiScope: string =
  process.env.REACT_APP_ORCHESTRATOR_API_SCOPE ?? "";
export const printerApiScope: string =
  process.env.REACT_APP_PRINTER_API_SCOPE ?? "";

export const msalConfig: Configuration = {
  auth: {
    clientId: clientId,
    authority: `https://login.microsoftonline.com/${tenantId}`,
    redirectUri: window.location.origin,
  },
  cache: {
    cacheLocation: "localStorage",
  },
};

// Singleton MSAL instance, created only when auth is enabled.
export const msalInstance: PublicClientApplication | null = authEnabled
  ? new PublicClientApplication(msalConfig)
  : null;

/**
 * Acquires an access token for the given scope.
 * Returns null when auth is disabled or no scope is configured.
 * Falls back to redirect-based interaction when silent acquisition fails.
 */
export async function acquireToken(scope: string): Promise<string | null> {
  if (!authEnabled || !msalInstance || !scope) {
    return null;
  }

  const account =
    msalInstance.getActiveAccount() ?? msalInstance.getAllAccounts()[0];

  if (!account) {
    // No signed-in account — trigger a full-page login.
    await msalInstance.loginRedirect({ scopes: [scope] });
    return null;
  }

  try {
    const result = await msalInstance.acquireTokenSilent({
      scopes: [scope],
      account: account,
    });
    return result.accessToken;
  } catch (error) {
    if (error instanceof InteractionRequiredAuthError) {
      await msalInstance.acquireTokenRedirect({
        scopes: [scope],
        account: account,
      });
      return null;
    }
    throw error;
  }
}
